/**
 * Pairing Code Utilities
 * Functions for generating and validating partner pairing codes
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Generate cryptographically secure 6-digit pairing code
 */
export function generatePairingCode(): string {
  // Use crypto API for secure random number generation
  if (typeof window !== 'undefined' && window.crypto) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const code = (array[0] % 900000) + 100000; // Ensures 6-digit number (100000-999999)
    return code.toString();
  }

  // Fallback for server-side
  const code = Math.floor(100000 + Math.random() * 900000);
  return code.toString();
}

/**
 * Validate pairing code format
 */
export function validatePairingCodeFormat(code: string): boolean {
  return /^\d{6}$/.test(code);
}

/**
 * Check if pairing code is expired
 */
export function isCodeExpired(expiresAt: string | Timestamp): boolean {
  const expiryDate = expiresAt instanceof Timestamp
    ? expiresAt.toDate()
    : new Date(expiresAt);

  return expiryDate < new Date();
}

/**
 * Calculate days until code expires
 */
export function daysUntilExpiry(expiresAt: string | Timestamp): number {
  const expiryDate = expiresAt instanceof Timestamp
    ? expiresAt.toDate()
    : new Date(expiresAt);

  const now = new Date();
  const diffTime = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Format pairing code for display (adds spaces for readability)
 */
export function formatPairingCode(code: string): string {
  if (code.length !== 6) return code;
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

/**
 * Remove formatting from code (for submission)
 */
export function unformatPairingCode(formattedCode: string): string {
  return formattedCode.replace(/\s+/g, '');
}
